import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import Button from "../../atomic/Button";
import {
  DropdownVariant,
  NavbarRendererVariant,
  type DropdownProps
} from "./types";
import { filterVisibleItems } from "./utils";
import { NavbarRenderer } from "./NavbarRenderer";

export const menuPanelClassName =
  "rounded-md bg-white py-1 text-gray-700 shadow-lg ring-1 ring-black ring-opacity-5";

const submenuPanelClassName = classNames(
  "invisible absolute left-full top-0 z-[60] min-w-[14rem] opacity-0 transition-all duration-100 group-hover:visible group-hover:opacity-100",
  menuPanelClassName
);

export function Dropdown({
  item,
  variant = DropdownVariant.Top,
  onNavigate
}: DropdownProps) {
  const children = filterVisibleItems(item.children ?? []);

  if (children.length === 0) return null;

  if (variant === DropdownVariant.Submenu) {
    return (
      <div className="group relative w-full">
        <div
          className="flex w-full cursor-default items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          role="button"
          tabIndex={0}
        >
          <span>{item.label}</span>
          <ChevronDownIcon
            className="ml-2 h-4 w-4 shrink-0 text-gray-700 -rotate-90"
            aria-hidden="true"
          />
        </div>
        <div className={submenuPanelClassName}>
          {children.map((child) => (
            <NavbarRenderer
              key={child.id}
              item={child}
              variant={NavbarRendererVariant.Menu}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    );
  }

  if (variant === DropdownVariant.Mobile) {
    return (
      <div className="py-1">
        <div
          className="px-4 py-2 text-sm font-semibold"
          style={{ color: "var(--navbar-text)" }}
        >
          {item.label}
        </div>
        <div className="pl-2">
          {children.map((child) => (
            <NavbarRenderer
              key={child.id}
              item={child}
              variant={NavbarRendererVariant.Mobile}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        as={Button}
        className="inline-flex items-center justify-center rounded-lg border-0 !bg-transparent px-3 py-2 text-sm font-medium shadow-none focus:outline-none"
        style={{ color: "var(--navbar-text)" }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "var(--navbar-text-hover)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--navbar-text)";
        }}
      >
        {item.label}
        <ChevronDownIcon className="ml-2 h-4 w-4" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={classNames(
            "absolute left-0 z-50 mt-1 w-56 origin-top-left focus:outline-none",
            menuPanelClassName
          )}
        >
          {children.map((child) => (
            <NavbarRenderer
              key={child.id}
              item={child}
              variant={NavbarRendererVariant.Menu}
              onNavigate={onNavigate}
            />
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
